import { useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Heart, Layers, RefreshCw } from "lucide-react";
import SEO, { serviceSchema, breadcrumbSchema } from "@/components/SEO";

const ERIKA_PORTRAIT = "/images/erika-portrait.jpg";
const IMG_DNA = "/images/dna-abstract.jpg";
const IMG_CLINIC = "/images/clinic.jpg";

const SERVICE_DATA = {
  initial: {
    title: "Initial",
    subtitle: "Consultation",
    tagline: "Where your personalised health journey begins",
    image: ERIKA_PORTRAIT,
    icon: Heart,
    price: "$400",
    duration: "60 min",
    overview: "A comprehensive 1:1 session to understand your health at the deepest level. We explore your full health history, identify key biological patterns, and determine the most effective path forward. This consultation provides clarity — and if deeper analysis is needed, we move into a structured programme.",
    includes: [
      "In-depth exploration of your health history, concerns, and lifestyle",
      "Identification of key biological patterns and potential root causes",
      "Assessment of your priorities, goals, and vision for your health",
      "Personalised recommendations for next steps",
      "Clear guidance on whether further testing or the Rejuven Programme is right for you",
    ],
    process: [
      { step: "01", title: "Apply", desc: "Submit a short application telling me about your health goals. I'll review and respond within 48 hours." },
      { step: "02", title: "Pre-Session Intake", desc: "You'll receive a detailed health questionnaire so we can make the most of our time together." },
      { step: "03", title: "The Consultation", desc: "We spend 60 minutes exploring your health from every angle — physical, functional, and systemic." },
      { step: "04", title: "Your Path Forward", desc: "You receive a summary and personalised recommendations for which services and protocols are the best fit." },
    ],
    forWho: "This consultation is for anyone ready to take a deeper, more intentional approach to their health. Whether you're dealing with specific concerns, optimising performance, navigating fertility, or seeking disease prevention — this session provides the clarity and direction you need. Please note: Erika is a UK-qualified health consultant and does not diagnose, treat, or prescribe as a licensed physician in the United States.",
  },
  rejuven: {
    title: "Personalised Longevity",
    subtitle: "& Wellness Programme",
    tagline: "From understanding your health to actively changing it",
    image: IMG_DNA,
    icon: Layers,
    price: "$3,500 – $5,000",
    duration: "Full Programme",
    overview: "The Rejuven Precision Programme is a complete, integrated analysis of your biology. This is where the real work happens — I analyse your data across multiple layers, integrate everything into a coherent biological picture, and deliver a structured, actionable plan designed specifically for your biology.",
    includes: [
      "Full genetics interpretation (e.g. 23andMe, Ancestry, specialised panels)",
      "Personalised nutrition and supplement strategy",
      "Microbiome analysis (Viome, GI-MAP, and other platforms)",
      "Clinical and pharmacogenomic integration",
      "Comprehensive health data integration across all data points",
      "Personalised protocol: nutrition, supplementation, microbiome modulation, lifestyle optimisation",
      "Follow-up session to review progress and refine your plan",
    ],
    process: [
      { step: "01", title: "Initial Consultation", desc: "We begin with a comprehensive session to understand your full picture (included or prerequisite)." },
      { step: "02", title: "Data Collection", desc: "Genetic testing, microbiome testing, and blood panels. I work with data from any laboratory — I do not sell tests." },
      { step: "03", title: "Precision Analysis", desc: "I analyse and integrate your data across genomics, microbiome, biomarkers, and health history into one coherent picture." },
      { step: "04", title: "Personalised Strategy", desc: "You receive a structured, actionable plan covering nutrition, supplementation, microbiome modulation, and long-term optimisation." },
      { step: "05", title: "Follow-Up", desc: "We review your progress, refine your protocol, and adapt as your biology evolves. Your genetic data remains a long-term asset." },
    ],
    forWho: "This programme is for those who are serious about understanding and actively shaping their health. Ideal for individuals seeking longevity and disease prevention, women navigating fertility, those with chronic unexplained symptoms, and high-performers who want deeper biological insight.",
  },
  followup: {
    title: "Follow-Up",
    subtitle: "Consultation",
    tagline: "Health is dynamic — your plan should be too",
    image: IMG_CLINIC,
    icon: RefreshCw,
    price: "$250",
    duration: "60 min",
    overview: "Ongoing support and optimisation as your biology evolves. Health is not static, and neither is your protocol. Follow-up consultations allow us to refine your strategy based on your response to interventions, new data, and evolving goals.",
    includes: [
      "Review of your progress since last session",
      "Assessment of how interventions are working",
      "Integration of any new test results or data",
      "Protocol refinement and adjustment",
      "Updated recommendations based on evolving goals",
    ],
    process: [
      { step: "01", title: "Preparation", desc: "Share any new test results, symptoms, or observations before our session." },
      { step: "02", title: "Review Session", desc: "We assess what's working, what needs adjustment, and where to focus next." },
      { step: "03", title: "Updated Plan", desc: "You receive an updated protocol reflecting your current biology and goals." },
    ],
    forWho: "For existing clients who have completed an initial consultation or the Rejuven Programme. As new questions or health goals arise, we revisit and build on your insights — allowing for ongoing precision without starting from scratch.",
  },
};

export default function ServiceDetailPage() {
  const { serviceId } = useParams();
  const service = SERVICE_DATA[serviceId];
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.5], [0.5, 0.8]);

  if (!service) {
    return (
      <main className="min-h-screen bg-[#FDFBF7] pt-28 pb-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-medium text-[#2D2D2D] mb-4">Service Not Found</h1>
          <Link to="/#pricing" className="text-[#D4AF37] underline underline-offset-4">View All Services</Link>
        </div>
      </main>
    );
  }

  const Icon = service.icon;

  return (
    <main data-testid={`service-page-${serviceId}`} className="bg-[#FDFBF7]">
      <SEO
        title={`${service.title} ${service.subtitle}`}
        path={`/services/${serviceId}`}
        description={service.overview.substring(0, 160)}
        jsonLd={[
          serviceSchema({ id: serviceId, title: `${service.title} ${service.subtitle}`, description: service.overview, price: service.price }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/#pricing" },
            { name: `${service.title} ${service.subtitle}`, path: `/services/${serviceId}` },
          ]),
        ]}
      />
      {/* Hero */}
      <section ref={heroRef} className="relative h-[70vh] min-h-[500px] overflow-hidden flex items-end">
        <motion.div style={{ y: imageY }} className="absolute inset-0 -top-20">
          <img src={service.image} alt={service.title} className="w-full h-[calc(100%+150px)] object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#1C1C1C]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pb-16 w-full">
          <Link to="/#pricing" data-testid="service-back-link" className="inline-flex items-center gap-2 text-sm text-[#FDFBF7]/60 hover:text-[#D4AF37] transition-colors mb-8">
            <ArrowLeft size={14} /> All Services
          </Link>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <Icon size={32} className="text-[#D4AF37] mb-4 stroke-[1.5]" />
            <h1 className="text-5xl md:text-7xl font-medium tracking-tight text-[#FDFBF7] leading-[1.1]">{service.title}</h1>
            <p className="font-['Cormorant_Garamond'] italic text-2xl md:text-3xl text-[#D4AF37] mt-2">{service.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Tagline */}
      <div className="bg-[#1C1C1C] py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="font-['Cormorant_Garamond'] italic text-xl md:text-2xl text-[#FDFBF7]/70 text-center">{service.tagline}</p>
        </div>
      </div>

      {/* Overview */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-6 mb-10">
              <span className="text-sm text-[#6B6B6B] border border-[rgba(45,45,45,0.12)] px-4 py-2">{service.duration}</span>
              <span className="text-sm font-medium text-[#D4AF37] border border-[#D4AF37]/30 px-4 py-2">{service.price}</span>
            </div>
            <p className="text-xl md:text-2xl font-light leading-relaxed text-[#2D2D2D]">{service.overview}</p>
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 md:py-32 bg-[#F5F2EB]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">Includes</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#2D2D2D] mb-12">What you receive</h2>
          </motion.div>
          <div className="space-y-6">
            {service.includes.map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="flex items-start gap-4">
                <div className="w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={14} className="text-white" />
                </div>
                <p className="text-base font-light leading-relaxed text-[#2D2D2D]">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">The Process</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#2D2D2D] mb-16">How it works</h2>
          </motion.div>
          <div className="space-y-0">
            {service.process.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="flex gap-8 py-10 border-t border-[rgba(45,45,45,0.08)]">
                <span className="text-4xl md:text-5xl font-medium text-[#E6DCCA] flex-shrink-0 w-16">{p.step}</span>
                <div>
                  <h3 className="text-xl font-medium text-[#2D2D2D] mb-2">{p.title}</h3>
                  <p className="text-base font-light leading-relaxed text-[#6B6B6B]">{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-24 md:py-32 bg-[#F5F2EB]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">Who It's For</p>
            <p className="text-xl md:text-2xl font-light leading-relaxed text-[#2D2D2D]">{service.forWho}</p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#1C1C1C]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <p className="font-['Cormorant_Garamond'] italic text-2xl md:text-3xl text-[#FDFBF7]/70 mb-8">Ready to begin?</p>
            <h2 className="text-3xl md:text-5xl font-medium tracking-tight text-[#FDFBF7] mb-10">
              Apply for {service.title} {service.subtitle}
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to={`/apply?service=${serviceId}`} data-testid={`service-book-btn-${serviceId}`} className="btn-gold inline-flex items-center gap-2">
                Apply Now <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="text-[#FDFBF7]/60 hover:text-[#D4AF37] underline decoration-1 underline-offset-4 transition-colors text-sm font-medium">
                Have Questions?
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
